import { getTagFromNode, useModuleFederationContext, useTranslation } from '@msdk/common'
import {
  ControlValidationType,
  Entity,
  FormListFieldDefinition,
  FormOpenMode,
  GenericFieldValue,
  ListFieldDefinition,
  ListValue,
} from '@msdk/definitions'
import { FormField, FormFieldLabel, TextInput } from '@msdk/ui'
import React from 'react'
import { useParams } from 'react-router-dom'

import { useFieldSeverity, useFieldValue, useHasMandatoryValidation } from '../../../hooks'
import { ChangeArgs, FieldProps } from '../../../models/form'
import { nodeHasFlag, resolveItemFromAll } from '../../../utils'
import { ValidationMessages } from '../../ValidationMessages'
import { ListTable } from './ListTable'

export function ListFields({ node, onChange, fieldArgs }: FieldProps) {
  const fieldDef = node.value.fieldDefinition as ListFieldDefinition
  const formDef = node.value.formDefinition as FormListFieldDefinition

  /**
   * Set the debounce dealy to 0 because the change on the inner fields is already
   * debounced, here the list is just relaying that change event to the form
   * so it does not need to be debounced again
   */
  const [value, setValue] = useFieldValue(node, onChange, 0)
  const t = useTranslation()
  const { moduleName } = useParams()

  const hasMandatoryFlag = nodeHasFlag(node, ControlValidationType.Mandatory)
  const hasMandatory = useHasMandatoryValidation(node, fieldDef.entityName)

  function handleOnFieldChange(args: ChangeArgs, rowId: string) {
    const v = new ListValue()
    v.mapField(value as ListValue, fieldDef)
    const row = v.value?.find((e) => e.id === rowId) as Entity
    row.fields[args.fieldName] = args.value
    node.value.isDirty = true
    setValue(v)
  }

  function handleOnDelete(nodeId: string): void {
    const v = new ListValue()
    if (value) v.mapField(value as ListValue, fieldDef)
    v.value = v.value?.filter((e) => e.id !== nodeId)
    node.value.isDirty = true
    setValue(v)
  }

  function handleOnAdd(): void {
    const v = new ListValue()
    if (value) v.mapField(value as ListValue, fieldDef)
    const initFields: Record<string, GenericFieldValue> = {}
    fieldDef.fields.forEach((e) => (initFields[e.name] = null))

    if (!v.value) {
      v.value = []
    }
    v.value?.push({
      id: crypto.randomUUID(),
      entityName: fieldDef.entityName,
      fields: initFields,
      isPersisted: false,
      isInCreation: false,
      parentId: node.value.entityId as string,
      canUpdate: false,
      canActivate: false,
      canDeactivate: false,
      canDelete: false,
    })

    node.value.isDirty = true
    setValue(v)
  }

  const modules = useModuleFederationContext()
  const CustomInputComponent = formDef.rendererUI ? resolveItemFromAll(modules, formDef.rendererUI) : undefined
  const inputComponent = CustomInputComponent ?? ListTable

  const isReadOnly = node.value.state === 'Readonly'
  const severity = useFieldSeverity(fieldDef, node)
  const required = (hasMandatory || hasMandatoryFlag) && fieldArgs?.formOpenMode !== FormOpenMode.Readonly

  return (
    <>
      <FormField
        className="FormField"
        $hasPrefix={!!fieldDef.prefixIcon}
        required={required}
        size="small"
        data-test-id={getTagFromNode(fieldDef, moduleName)}
        severity={severity}
        readOnly={isReadOnly}
        disabled={isReadOnly}
      >
        <FormFieldLabel shrink required={required}>
          {t(fieldDef.idml, fieldDef.description)}
        </FormFieldLabel>
        <TextInput
          notched
          disabled
          label={t(fieldDef.idml, fieldDef.description)}
          required={required}
          inputComponent={inputComponent}
          inputProps={{
            fieldDef,
            formDef,
            onFieldChange: handleOnFieldChange,
            onRowAdd: handleOnAdd,
            onRowDelete: handleOnDelete,
            node,
            fieldArgs,
            moduleName,
          }}
        />
        <ValidationMessages messages={node.value.validationMessages} fieldDef={fieldDef} node={node} />
      </FormField>
    </>
  )
}
